import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';

const trimmedNonEmpty = z
  .string()
  .min(1)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

/**
 * @aiPurpose canonical wire shape for a Packaging option row (#995).
 *   The directory holds packaging option labels only — no description,
 *   shortName, or multilingual fields by design.
 * @aiInvariant `name` is trimmed and rejected when blank. DB enforces
 *   active-only normalized uniqueness via a partial unique index on
 *   `lower(name) WHERE active = true` (declared in
 *   `20260522000000_add_road_packaging_dictionaries`); restoring an
 *   archived duplicate while an active duplicate exists surfaces
 *   `DOMAIN_CONFLICT`. List ordering is `(sortOrder ASC, id ASC)`.
 * @aiSecurity none — reference data, no PII surface.
 */
export const PackagingOptionSchema = z
  .object({
    id: z.string().uuid(),
    name: trimmedNonEmpty,
    active: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type PackagingOption = z.infer<typeof PackagingOptionSchema>;

/**
 * @aiPurpose `POST /api/packaging-options` body. `active` defaults to
 *   `true`, `sortOrder` defaults to `0`.
 */
export const CreatePackagingOptionInputSchema = z
  .object({
    name: trimmedNonEmpty,
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
  })
  .strict();
export type CreatePackagingOptionInput = z.infer<typeof CreatePackagingOptionInputSchema>;

/**
 * @aiPurpose `PATCH /api/packaging-options/:id` body. `active` is
 *   reserved for the dedicated archive/activate endpoints; the patch
 *   shape intentionally only exposes business fields (`name`,
 *   `sortOrder`) so the lifecycle ledger never collides with field
 *   updates.
 */
export const PatchPackagingOptionInputSchema = nonEmptyPatch(
  z
    .object({
      name: trimmedNonEmpty,
      sortOrder: z.number().int().min(0),
    })
    .partial()
    .strict(),
);
export type PatchPackagingOptionInput = z.infer<typeof PatchPackagingOptionInputSchema>;

const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/packaging-options`. `q` matches
 *   `name` with case-insensitive substring semantics.
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to
 *   `50`, max `500`.
 */
export const GetPackagingOptionsQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    active: stringBoolean.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(50),
  })
  .strict();
export type GetPackagingOptionsQuery = z.infer<typeof GetPackagingOptionsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/packaging-options`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying
 *   query returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(sortOrder, id)` of the last returned row.
 */
export const GetPackagingOptionsResponseSchema = z
  .object({
    items: z.array(PackagingOptionSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetPackagingOptionsResponse = z.infer<typeof GetPackagingOptionsResponseSchema>;
